import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Button,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import DataTable from "react-data-table-component";
import axios from "axios";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./TransactionView.css";
import { createContext } from "react";

// Sample data for transaction history (fallback if API fails)
const transactionData = [
  {
    id: 1,
    type: "Credit",
    amount: "LKR 8900.00",
    status: "Pending",
    description: "Payment to ABC Store...",
  },
  // ... other transactions
];

// Columns for the DataTable
const columns = [
  { name: "Transaction Type", selector: (row) => row.type, sortable: true },
  { name: "Amount", selector: (row) => row.amount, sortable: true },
  {
    name: "Status",
    selector: (row) => row.status,
    sortable: true,
    cell: (row) => (
      <span
        className={` ${
          row.status === "Completed"
            ? "status-button-success"
            : "status-button-warning"
        }`}
      >
        {row.status}
      </span>
    ),
  },
  { name: "Description", selector: (row) => row.description, sortable: true },
  { name: "Date", selector: (row) => row.createdAt, sortable: true },
];

// Custom styles for the DataTable
const customStyles = {
  headCells: {
    style: {
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
  cells: {
    style: {
      fontSize: "14px",
    },
  },
};

const TransactionView = () => {
  // State for modals
  const [addCardModal, setAddCardModal] = useState(false);
  const [reloadModal, setReloadModal] = useState(false);
  // State for cards, wallet, transactions, error, and selected card
  const [cards, setCards] = useState([]);
  const [wallet, setWallet] = useState({ balance: "LKR 0.00" });
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  // Assume userId is available (e.g., from auth context or props)
  const userId = "12345"; // Replace with actual userId from authentication

  // Fetch cards, wallet, and transactions on component mount
  useEffect(() => {
    let mounted = true;

    // Fetch cards
    axios
      .get(`https://api.starmarketapi.online/wallet1/api/cards/${userId}`)
      .then((response) => {
        if (mounted) {
          console.log("Cards fetched:", response.data);
          const fetchedCards = Array.isArray(response.data)
            ? response.data
            : [];
          setCards(fetchedCards);

          // Set default selected card (first card if available and has cardId)
          if (fetchedCards.length > 0 && fetchedCards[0]?._id) {
            console.log("Cards fetched:", fetchedCards[0]._id);
            setSelectedCardId(fetchedCards[0]._id);
          }
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error("Error fetching cards:", err);
          setError("Failed to fetch cards");
        }
      });

    // Fetch wallet details
    axios
      .get(`https://api.starmarketapi.online/wallet1/api/wallet/${userId}`)
      .then((response) => {
        if (mounted) {
          setWallet({
            balance: `LKR ${response.data.balance?.toFixed(2) || "0.00"}`,
          });
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error("Error fetching wallet:", err);
          setError("Failed to fetch wallet details");
        }
      });

    // Fetch transactions
    axios
      .get(
        `https://api.starmarketapi.online/wallet1/api/transactions/${userId}`
      )
      .then((response) => {
        console.log("Transactions fetched:", response.data);
        if (mounted) {
          setTransactions(
            Array.isArray(response.data)
              ? response.data.map((tx) => ({
                  ...tx,
                  amount: `LKR ${parseFloat(tx.amount).toFixed(2)}`,
                }))
              : []
          );
        }
      })
      .catch((err) => {
        if (mounted) {
          console.error("Error fetching transactions:", err);
          setError("Failed to fetch transactions");
        }
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  // Toggle functions for modals
  const toggleAddCardModal = () => setAddCardModal(!addCardModal);
  const toggleReloadModal = () => setReloadModal(!reloadModal);

  // Handle form submission for adding a card
  const handleAddCardSubmit = async (e) => {
    e.preventDefault();
    const cardData = {
      userId,
      cardNumber: e.target.cardNumber.value,
      cardHolder: e.target.cardHolder.value,
      expiryDate: e.target.expiryDate.value,
      cvv: e.target.cvv.value,
      cardType: e.target.cardType.value, // Add cardType to cardData
    };

    try {
      const response = await axios.post(
        "https://api.starmarketapi.online/wallet1/api/cards",
        cardData
      );
      console.log("Card Added:", response.data);
      if (response.data?._id) {
        setCards([...cards, response.data]);
        setSelectedCardId(response.data._id);
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Card added successfully!",
          timer: 2000,
          showConfirmButton: false,
        });
        toggleAddCardModal();
      } else {
        throw new Error("Invalid card data returned from server");
      }
    } catch (err) {
      console.error("Error adding card:", err.response?.data || err.message);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          "Failed to add card: " +
          (err.response?.data?.errors?.[0]?.msg || err.message),
      });
    }
  };

  // Handle form submission for reloading wallet
  const handleReloadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCardId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a card to reload",
      });
      return;
    }

    const reloadData = {
      userId,
      amount: parseFloat(e.target.reloadAmount.value),
      cardId: selectedCardId,
    };

    try {
      const response = await axios.post(
        "https://api.starmarketapi.online/wallet1/api/wallet/topup",
        reloadData
      );
      console.log("Wallet Reloaded:", response.data);
      setWallet({
        balance: `LKR ${response.data.balance?.toFixed(2) || "0.00"}`,
      });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Wallet reloaded successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
      toggleReloadModal();
    } catch (err) {
      console.error(
        "Error reloading wallet:",
        err.response?.data || err.message
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          "Failed to reload wallet: " +
          (err.response?.data?.errors?.[0]?.msg || err.message),
      });
    }
  };

  // Select the card to display (based on selectedCardId)
  const selectedCard =
    cards.find((card) => card._id === selectedCardId) ||
    (cards.length > 0 && cards[0]?._id ? cards[0] : null);

  console.log("Selected Card:", selectedCardId);

  return (
    <React.Fragment>
      <section className="section gaps bg-light" style={{ paddingTop: "80px" }}>
        <div className="transaction-view">
          {error && <div className="alert alert-danger">{error}</div>}
          <Row>
            {/* Left Card Section */}
            <Col md="3">
              <Card className="credit-card">
                <CardBody>
                  <div className="card-header ms-card">
                    <img
                      src={
                        selectedCard?.cardType === "Visa"
                          ? "https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png"
                          : "https://upload.wikimedia.org/wikipedia/commons/0/04/Mastercard-logo.png"
                      }
                      alt={selectedCard?.cardType || "Card"}
                      className="mastercard-logo"
                    />
                    <div className="card-number">
                      {selectedCard ? (
                        <>
                          <h5>{selectedCard.cardHolder}</h5>**** **** **** *
                          {selectedCard.cardNumberLastThreeDigits}
                        </>
                      ) : (
                        "No Card Selected"
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="card-amount text-center">
                      <h6>AMOUNT</h6>
                      <h4>{wallet.balance}</h4>
                    </div>
                  </div>

                  <div className="add-card">
                    <hr />
                    <Button color="link" onClick={toggleAddCardModal}>
                      Add New Card +
                    </Button>
                    <hr />
                  </div>

                  <div className="spending-details">
                    <div className="category-header">Credit/Debit Card</div>
                    <div className="category-items">
                      {cards.length > 0 ? (
                        <FormGroup>
                          <Label for="cardSelect">Select Card</Label>
                          <Input
                            type="select"
                            id="cardSelect"
                            value={selectedCardId || ""}
                            onChange={(e) =>
                              setSelectedCardId(e.target.value || null)
                            }
                          >
                            <option value="">Select a card</option>
                            {cards.map((card) => (
                              <option key={card._id} value={card._id}>
                                **** **** **** *{card.cardNumberLastThreeDigits}{" "}
                                ({card.cardHolder} - {card.cardType})
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      ) : (
                        <div>No cards available</div>
                      )}
                    </div>

                    <div className="category-header">Mobile Wallet</div>
                    <div className="category-items">
                      <div className="item">
                        <img
                          className="icon-placeholder"
                          src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg"
                          alt="PayPal"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    block
                    className="reload-button"
                    onClick={toggleReloadModal}
                  >
                    TOP UP WALLET
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Right Transaction History Section */}
            <Col md="9">
              <Card>
                <CardBody>
                  <div className="transaction-history">
                    <div className="table-header">
                      <h4>Transaction History</h4>
                    </div>
                    <DataTable
                      columns={columns}
                      data={
                        transactions.length > 0 ? transactions : transactionData
                      }
                      customStyles={customStyles}
                      pagination
                      highlightOnHover
                      striped
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add New Card Modal */}
          <Modal isOpen={addCardModal} toggle={toggleAddCardModal} centered>
            <ModalHeader
              style={{ borderBottom: "none" }}
              toggle={toggleAddCardModal}
            ></ModalHeader>
            <ModalBody>
              <div style={{ textAlign: "center", marginBottom: "50px" }}>
                <h5>Add New Card</h5>
              </div>
              <Form onSubmit={handleAddCardSubmit}>
                <FormGroup>
                  <Label for="cardType">Card Type</Label>
                  <div className="card-type-selection">
                    <FormGroup check inline>
                      <Input
                        type="radio"
                        name="cardType"
                        value="Visa"
                        id="cardTypeVisa"
                        required
                      />
                      <Label check for="cardTypeVisa">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/4/41/Visa_Logo.png"
                          alt="Visa"
                          className="card-type-logo"
                        />
                      </Label>
                    </FormGroup>
                    <FormGroup check inline>
                      <Input
                        type="radio"
                        name="cardType"
                        value="MasterCard"
                        id="cardTypeMasterCard"
                        required
                      />
                      <Label check for="cardTypeMasterCard">
                        <img
                          src="https://upload.wikimedia.org/wikipedia/commons/0/04/Mastercard-logo.png"
                          alt="MasterCard"
                          className="card-type-logo"
                        />
                      </Label>
                    </FormGroup>
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label for="cardNumber">Card Number</Label>
                  <Input
                    type="text"
                    id="cardNumber"
                    placeholder="Enter 16-digit card number"
                    maxLength="16"
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="cardHolder">Cardholder Name</Label>
                  <Input
                    type="text"
                    id="cardHolder"
                    placeholder="Enter cardholder name"
                    required
                  />
                </FormGroup>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label for="expiryDate">Expiry Date</Label>
                      <Input
                        type="text"
                        id="expiryDate"
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label for="cvv">CVV</Label>
                      <Input
                        type="text"
                        id="cvv"
                        placeholder="Enter CVV"
                        maxLength="3"
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <ModalFooter>
                  <button className="btn-addcard" type="submit">
                    Add Card
                  </button>
                </ModalFooter>
              </Form>
            </ModalBody>
          </Modal>

          {/* Reload Amount Modal */}
          <Modal isOpen={reloadModal} toggle={toggleReloadModal} centered>
            <ModalHeader
              style={{ borderBottom: "none" }}
              toggle={toggleReloadModal}
            ></ModalHeader>
            <ModalBody>
              <div style={{ textAlign: "center", marginBottom: "50px" }}>
                <h5> TOP UP WALLET</h5>
              </div>
              <Form onSubmit={handleReloadSubmit}>
                <FormGroup>
                  <Label for="reloadAmount">Amount (LKR)</Label>
                  <Input
                    type="number"
                    id="reloadAmount"
                    placeholder="Enter amount to reload"
                    min="1"
                    required
                  />
                </FormGroup>
                <ModalFooter>
                  <button className="btn-addcard" type="submit">
                    Reload
                  </button>
                </ModalFooter>
              </Form>
            </ModalBody>
          </Modal>
        </div>
      </section>
    </React.Fragment>
  );
};

export default TransactionView;
